/**
 * صورة السجل.
 *
 * صورةٌ للعميل المحتمل تحلّ محلّ حروف اسمه. والحروف تبقى بديلًا لا يُلغى:
 * أكثر السجلات بلا صورة، وصفٌّ فارغ المكان أسوأ من حرفين ملوَّنين.
 *
 * ## تُخزَّن نصًّا لا مرفقًا
 *
 * المرفقات محجوبة خلف المصادقة وتُقرأ عبر `blob:` بطلبٍ لكل ملف. وصفحةٌ
 * فيها خمسة وعشرون صفًّا تعني خمسة وعشرين طلبًا مصادَقًا قبل أن تظهر
 * الصورة الأولى.
 *
 * فتُخزَّن `data:` في عمودٍ نصّي — كما يُخزَّن شعار الشركة وختمها منذ
 * البداية. والثمن حجمُ الصفّ، ولذلك تُصغَّر إلى مربّعٍ صغير قبل الحفظ:
 * الصورة الأصلية من الهاتف ثلاثة ميجابايت، والمعروض منها ثمانية وعشرون
 * بكسلًا في الجدول.
 *
 * ## والسحب من البريد يقع مرة
 *
 * حين يُحفظ السجل لا حين يُعرض. البديل — رابطٌ إلى خدمة خارجية يُحفظ
 * ويُطلب في كل رسم — يكشف عنوان كل من يفتح الشاشة لتلك الخدمة، ويجعل
 * صورة العميل تختفي إن سقطت.
 */
import { z } from 'zod';

/* ==========================================================================
   الحدود
   ========================================================================== */

/** طول ضلع الصورة المخزَّنة بالبكسل. */
export const AVATAR_PX = 128;

/**
 * أقصى حجم للصورة المخزَّنة.
 *
 * مربّعٌ بمئة وثمانية وعشرين بكسلًا بصيغة JPEG يقع تحت عشرة كيلوبايتات.
 * والحدّ أربعون: يتّسع لما لا يُضغط جيدًا، ويردّ من يحاول حشر صورة كاملة
 * في عمودٍ يُقرأ خمسًا وعشرين مرة في الصفحة.
 */
export const MAX_AVATAR_BYTES = 40_000;

/**
 * الصيغ المقبولة.
 *
 * ولا SVG — بخلاف شعار الشركة. الشعار يرفعه صاحب الحساب مرة، والصورة هنا
 * قد تأتي من استيراد أو من خدمة خارجية، وSVG مستندٌ يحمل نصًّا برمجيًّا
 * يُنفَّذ إن عُرض في سياقٍ خاطئ.
 */
const AVATAR_MIME = /^data:image\/(png|jpeg|jpg|webp);base64,/;

export const avatarDataUrl = z
  .string()
  .trim()
  .max(MAX_AVATAR_BYTES, 'حجم الصورة أكبر من الحدّ المسموح.')
  .refine((v) => v === '' || AVATAR_MIME.test(v), {
    message: 'الصورة يجب أن تكون PNG أو JPEG أو WebP.',
  });

/** الحقل كما يصل في نماذج الإنشاء والتعديل. */
export const AvatarFieldSchema = avatarDataUrl.nullable().optional();

/* ==========================================================================
   السحب من البريد
   ========================================================================== */

/**
 * ما يقع حين يُطلب سحبُ الصورة من البريد.
 *
 * يُعاد للشاشة لتقول ما جرى بدل أن تصمت: «لا صورة لهذا البريد» حالةٌ
 * شائعة جدًّا، ومن لا يعرفها يظنّ الزرّ معطّلًا فيضغطه مرارًا.
 */
export type AvatarFetchOutcome = 'found' | 'none' | 'no-email' | 'disabled' | 'failed';

export const AVATAR_FETCH_MESSAGES: Readonly<Record<AvatarFetchOutcome, string>> = {
  found: 'وُجدت صورة لهذا البريد.',
  none: 'لا صورة عامة مرتبطة بهذا البريد. ارفع صورة أو اترك الحروف.',
  'no-email': 'اكتب البريد الإلكتروني أولًا.',
  disabled: 'السحب من البريد مُعطَّل من إعدادات الشركة.',
  failed: 'تعذّر الوصول إلى الخدمة. حاول لاحقًا أو ارفع صورة.',
};

export interface AvatarFetchResult {
  outcome: AvatarFetchOutcome;
  /** الصورة إن وُجدت — `data:` جاهزة للحفظ. */
  avatarUrl: string | null;
}

export const FetchAvatarSchema = z.object({
  email: z.string().trim().toLowerCase().email('البريد غير صحيح.').max(200),
});
export type FetchAvatarInput = z.infer<typeof FetchAvatarSchema>;
